/**
 * /rolling-monthly-broadband — SEO landing page (owner 2026-10-16: "aggressively update seo
 * with rolling monthly broadband plans").
 * Targets: "rolling monthly broadband", "1 month broadband contract", "30 day broadband",
 * "month to month broadband", "short term broadband", "flexible broadband".
 * Facts live in data/rollingPlans.ts — it IS a contract (1-month minimum, 30 days' notice).
 */
import { Link } from 'react-router-dom'
import { CheckCircle, ArrowRight, Wifi, Calendar, Shield, MapPin, RefreshCw } from 'lucide-react'
import SEOHead from '@/components/SEOHead'
import { ROLLING, rollingFaqs } from '@/data/rollingPlans'

const FAQS = rollingFaqs()

const PLANS = [
  {
    name: 'Broadband Lite — Rolling Monthly',
    price: `£${ROLLING.litePrice}`,
    deposit: `£${ROLLING.liteDeposit} refundable holding deposit`,
    speed: '10–50 Mbps on 4G',
    to: '/broadband/lite?term=1',
    features: ['Unlimited data', '4G router included', '1-month minimum term', `${ROLLING.notice}' notice to cancel`],
  },
  {
    name: '5G Premium — Rolling Monthly',
    price: `£${ROLLING.premiumPrice}`,
    deposit: `£${ROLLING.premiumDeposit} refundable holding deposit`,
    speed: '80–400 Mbps on 5G (50–150 Mbps on 4G)',
    to: '/broadband/premium?term=1',
    popular: true,
    features: ['Unlimited 5G data', '5G router included', '1-month minimum term', `${ROLLING.notice}' notice to cancel`],
  },
]

const TERMS = [
  { term: '1 month (rolling)', lite: `£${ROLLING.litePrice}`, premium: `£${ROLLING.premiumPrice}`, note: `Leave any time with ${ROLLING.notice}' notice` },
  { term: '3 months', lite: '£19.95', premium: '£32.95', note: 'A season or a short let' },
  { term: '12 months', lite: '£17.59', premium: '£27.95', note: 'A year, lower price' },
  { term: '24 months', lite: '£15.95', premium: '£21.95', note: 'Best value Lite' },
  { term: '36 months', lite: '—', premium: '£19.95', note: 'Best value 5G' },
]

export default function RollingMonthlyBroadband() {
  const url = 'https://www.siluxmobile.co.uk/rolling-monthly-broadband'
  const breadcrumbs = [
    { name: 'Home', url: 'https://www.siluxmobile.co.uk/' },
    { name: 'Broadband', url: 'https://www.siluxmobile.co.uk/broadband' },
    { name: 'Rolling Monthly Broadband', url },
  ]
  const offerSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Rolling Monthly Broadband',
    serviceType: 'Mobile Broadband',
    provider: { '@type': 'Organization', name: 'Silux Mobile', url: 'https://www.siluxmobile.co.uk' },
    areaServed: { '@type': 'Country', name: 'United Kingdom' },
    url,
    offers: PLANS.map((p) => ({
      '@type': 'Offer',
      name: p.name,
      price: p.price.replace('£', ''),
      priceCurrency: 'GBP',
      availability: 'https://schema.org/InStock',
      url: 'https://www.siluxmobile.co.uk' + p.to,
      priceSpecification: {
        '@type': 'UnitPriceSpecification', price: p.price.replace('£', ''), priceCurrency: 'GBP',
        unitCode: 'MON', referenceQuantity: { '@type': 'QuantitativeValue', value: 1, unitCode: 'MON' },
      },
    })),
  }

  return (
    <>
      <SEOHead
        title={`Rolling Monthly Broadband | 1 Month Contract From £${ROLLING.litePrice} | Silux Mobile`}
        description={`Rolling monthly 4G & 5G broadband with a 1-month minimum term and ${ROLLING.notice}' notice to cancel. Broadband Lite from £${ROLLING.litePrice}/month, 5G Premium from £${ROLLING.premiumPrice}/month — router included, no landline, no engineer, free Tracked 48 delivery.`}
        keywords={[
          'rolling monthly broadband', 'rolling broadband', '1 month broadband contract', 'one month broadband',
          '30 day broadband', '30 day rolling broadband', 'month to month broadband', 'monthly rolling broadband',
          'short term broadband', 'flexible broadband', 'broadband no long contract', 'temporary broadband',
          'short term internet', 'rolling monthly 5g broadband', 'rolling 4g broadband', 'monthly broadband uk',
          'broadband for short lets', 'broadband for renters', 'no landline broadband', 'mobile broadband rolling contract',
        ]}
        canonical={url}
        breadcrumbs={breadcrumbs}
        faqs={FAQS}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(offerSchema) }} />

      {/* Hero */}
      <section className="relative py-20 px-4 bg-gradient-to-br from-indigo-700 via-indigo-600 to-teal-600 text-white overflow-hidden">
        <div className="max-w-5xl mx-auto relative text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-2 mb-6 text-sm font-medium">
            <RefreshCw className="w-4 h-4" />
            New — broadband from 1 month
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            Rolling Monthly Broadband
            <span className="block text-2xl md:text-3xl font-normal text-white/80 mt-2">
              1-month contract from £{ROLLING.litePrice}/month — leave with {ROLLING.notice}' notice
            </span>
          </h1>
          <p className="text-lg md:text-xl text-white/80 max-w-2xl mx-auto mb-8">
            4G and 5G home broadband on a rolling monthly contract. No long minimum term, no landline and no engineer —
            the router is included and delivered free by Royal Mail Tracked 48.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/broadband/lite?term=1" className="inline-flex items-center justify-center gap-2 bg-white text-indigo-800 hover:bg-indigo-50 font-bold px-8 py-4 rounded-xl text-lg">
              Rolling Broadband Lite <ArrowRight className="w-5 h-5" />
            </Link>
            <Link to="/broadband/premium?term=1" className="inline-flex items-center justify-center gap-2 border-2 border-white text-white hover:bg-white/10 font-bold px-8 py-4 rounded-xl text-lg">
              Rolling 5G Premium <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <div className="flex flex-wrap justify-center gap-6 mt-8 text-sm text-white/80">
            <span className="flex items-center gap-2"><Calendar className="w-4 h-4 text-green-300" /> Plans from 1 month</span>
            <span className="flex items-center gap-2"><Shield className="w-4 h-4 text-green-300" /> No setup fee</span>
            <span className="flex items-center gap-2"><Wifi className="w-4 h-4 text-green-300" /> Unlimited data</span>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-14 px-4 bg-white">
        <div className="max-w-4xl mx-auto text-gray-700 space-y-4 text-lg">
          <p>
            <strong>Rolling monthly broadband</strong> gives you home internet without tying you in. Your Silux Mobile
            plan has a 1-month minimum term; after that it simply rolls on month to month until you tell us to stop,
            with {ROLLING.notice}' notice. It is ideal if you are renting, between homes, running a short let, working on
            a project away from home, or just want to try mobile broadband before committing.
          </p>
          <p>
            It is a proper broadband contract — signed online and paid monthly by Direct Debit — on the Three and O2
            4G/5G networks. Prefer a lower monthly price? Every package is also sold on fixed terms of 3, 6, 12, 18,
            24 and 36 months, and you can move from rolling monthly to a longer plan from your account at any time.
          </p>
        </div>
      </section>

      {/* Plans */}
      <section className="py-14 px-4 bg-indigo-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-3">Rolling Monthly Broadband Plans</h2>
          <p className="text-gray-500 text-center mb-10">Prices on Three. 1-month minimum term, then month to month. Router included.</p>
          <div className="grid md:grid-cols-2 gap-6">
            {PLANS.map((p) => (
              <div key={p.name} className={`bg-white rounded-2xl p-6 border-2 ${p.popular ? 'border-indigo-500 shadow-lg' : 'border-gray-200'}`}>
                {p.popular && <span className="inline-block bg-indigo-600 text-white text-xs font-bold px-3 py-1 rounded-full mb-3">Fastest</span>}
                <h3 className="text-xl font-bold text-gray-900">{p.name}</h3>
                <p className="text-4xl font-extrabold text-indigo-700 mt-3">{p.price}<span className="text-base font-medium text-gray-500">/month</span></p>
                <p className="text-sm text-gray-500 mt-1">+ {p.deposit}</p>
                <p className="text-sm text-gray-600 mt-2">Typical speed: {p.speed}</p>
                <ul className="mt-4 space-y-2">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-gray-700"><CheckCircle className="w-4 h-4 text-indigo-500 shrink-0" />{f}</li>
                  ))}
                </ul>
                <Link to={p.to} className={`mt-6 block text-center font-bold py-3 rounded-xl ${p.popular ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-gray-900 text-white hover:bg-gray-800'}`}>
                  Choose this plan
                </Link>
              </div>
            ))}
          </div>
          <p className="text-sm text-gray-500 text-center mt-6">
            On O2: Broadband Lite £{ROLLING.o2LitePrice}/month, 5G Premium £{ROLLING.o2PremiumPrice}/month.{' '}
            <Link to="/network-coverage" className="text-indigo-700 underline">Check coverage at your postcode</Link> before you order.
          </p>
        </div>
      </section>

      {/* Term comparison */}
      <section className="py-14 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-3">Every Plan Length, From 1 Month</h2>
          <p className="text-gray-500 text-center mb-8">Monthly prices on Three. The longer the term, the lower the price.</p>
          <div className="overflow-x-auto">
            <table className="w-full text-left border border-gray-200 rounded-xl overflow-hidden">
              <thead className="bg-gray-50 text-sm text-gray-600">
                <tr><th className="p-3">Plan length</th><th className="p-3">Broadband Lite</th><th className="p-3">5G Premium</th><th className="p-3 hidden sm:table-cell">Good for</th></tr>
              </thead>
              <tbody>
                {TERMS.map((t, i) => (
                  <tr key={t.term} className={`border-t ${i === 0 ? 'bg-indigo-50 font-semibold' : ''}`}>
                    <td className="p-3">{t.term}</td><td className="p-3">{t.lite}</td><td className="p-3">{t.premium}</td>
                    <td className="p-3 text-sm text-gray-600 hidden sm:table-cell">{t.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-500 mt-3">Short plans take a refundable holding deposit, returned at the end of your contract. No setup fee on any plan. All broadband packages include unlimited data.</p>
        </div>
      </section>

      {/* Who it's for */}
      <section className="py-14 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-10">Who Rolling Monthly Broadband Suits</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { t: 'Renters', b: 'No landline to install and nothing to leave behind — take the router with you when you move.', to: '/broadband-renters' },
              { t: 'Holiday homes & lets', b: 'Switch on for the season and give notice when it ends.', to: '/broadband-holiday-home' },
              { t: 'Caravans & touring', b: 'Portable 4G/5G internet that works wherever there is signal.', to: '/broadband-caravan' },
              { t: 'Moving home', b: 'Stay online between houses while you wait for a fixed line.', to: '/mobile-broadband' },
              { t: 'Rural homes', b: 'Try 4G/5G broadband where fibre is slow, with no long tie-in.', to: '/broadband-rural-uk' },
              { t: 'Projects & site work', b: 'Internet for a few months on site, then simply give notice.', to: '/broadband' },
            ].map((x) => (
              <Link key={x.t} to={x.to} className="bg-white rounded-xl p-5 border border-gray-200 hover:border-indigo-400">
                <h3 className="font-bold text-gray-900">{x.t}</h3>
                <p className="text-sm text-gray-600 mt-1">{x.b}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 px-4 bg-white">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">Rolling Monthly Broadband FAQs</h2>
          <div className="space-y-4">
            {FAQS.map((f) => (
              <details key={f.question} className="border border-gray-200 rounded-xl p-5 group" open>
                <summary className="font-semibold text-gray-900 cursor-pointer">{f.question}</summary>
                <p className="text-gray-700 mt-3">{f.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 px-4 bg-indigo-700 text-white text-center">
        <h2 className="text-3xl font-bold mb-3">Get online this week — on your terms</h2>
        <p className="text-white/80 mb-6">Free Royal Mail Tracked 48 delivery. Plug in the router and you are online in minutes.</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/broadband?term=1" className="inline-flex items-center justify-center gap-2 bg-white text-indigo-800 font-bold px-8 py-3 rounded-xl">See rolling plans <ArrowRight className="w-5 h-5" /></Link>
          <Link to="/network-coverage" className="inline-flex items-center justify-center gap-2 border-2 border-white font-bold px-8 py-3 rounded-xl"><MapPin className="w-5 h-5" /> Check coverage</Link>
        </div>
      </section>
    </>
  )
}
